import React, { useEffect, useState } from "react";
import UserServices from "../../services/UserServices";
import { Checkbox, Col, Input, Row, Typography, Form, message } from "antd";
import {
  WarningOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  MailOutlined,
} from "@ant-design/icons";
import PrimaryButton from "../Buttons/PrimaryButton";
import { translate } from "../../locale";
import DashServices from "../../services/DashServices";

const { Title } = Typography;

const RangeForm = ({ initialValues, title, onlyMax }) => {
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState(initialValues);
  const [checkboxValues, setCheckboxValues] = useState({
    patient: initialValues.patient,
    type: initialValues.type,
    max_value_alert: false,
    max_value: false,
    min_value: false,
    min_value_alert: false,
  });
  const [error, setError] = useState("");

  useEffect(() => {
    getRanges();
  }, []);

  const getRanges = async () => {
    try {
      setLoading(true);
      const r = await UserServices.getPatientRange(initialValues.patient);
      if (r.status === 200) {
        const ranges = r.data.ranges
          .filter((i) => i.type === initialValues.type)
          .pop();
        setValues((v) => ({
          ...v,
          ...ranges,
        }));

        if (ranges?.max_value_alert_check !== undefined) {
          setCheckboxValues((v) => ({
            ...v,
            max_value_alert: ranges.max_value_alert_check,
            max_value: ranges.max_value_check,
            min_value: ranges.min_value_check,
            min_value_alert: ranges.min_value_alert_check,
          }));
        } else {
          console.log(
            `No está configurado el rango para ${initialValues.type}`
          );
        }
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
  };

  const handleSaveRange = async () => {
    setLoading(true);
    try {
      const res = await DashServices.updateRange(values);
      const resCheck = await DashServices.updateCheckboxRange(checkboxValues);
      if (res.data.message === "ok" && resCheck.data.message === "ok") {
        message.success("Los rangos han sido actualizados exitosamente.");
      } else {
        setError(res.data.message);
      }
      setLoading(false);
    } catch (error) {
      console.log(error);
      setError(error);
      setLoading(false);
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setCheckboxValues((v) => ({ ...v, [name]: checked }));
  };

  const Label = ({ checkbox, type }) => {
    let bgDarkColor = "";
    let bgLightColor = "";
    let icon = null;
    if (type === "exclamation") {
      bgDarkColor = "bg-custom_darkred";
      bgLightColor = "bg-custom_red";
      icon = (
        <ExclamationCircleOutlined className="text-2xl lg:text-4xl text-white ml-2 lg:ml-4 leading-3" />
      );
    } else if (type === "warning") {
      bgDarkColor = "bg-custom_darkyellow";
      bgLightColor = "bg-custom_yellow";
      icon = (
        <WarningOutlined className="text-2xl lg:text-4xl text-white ml-2 lg:ml-4 leading-3" />
      );
    } else if (type === "check") {
      bgDarkColor = "bg-custom_green";
      bgLightColor = "bg-custom_green";
      icon = (
        <CheckCircleOutlined className="text-2xl lg:text-4xl text-white ml-2 lg:ml-4 leading-3" />
      );
    }

    return (
      <Row justify="start" className="mb-2" style={{ height: 68 }}>
        <Col
          className={`flex items-center rounded-tl rounded-bl py-4 px-2 lg:px-8 w-16 lg:w-32 ${bgDarkColor}`}
        >
          {checkbox ? (
            <>
              <MailOutlined className="text-2xl lg:text-4xl text-white mr-2 leading-3" />
              <Checkbox
                name={checkbox.name}
                onChange={checkbox.onChange}
                checked={checkbox.checked}
                value={checkbox.value}
              />
            </>
          ) : (
            <span className="text-white">{translate("Normal")}</span>
          )}
        </Col>
        <Col
          className={`flex items-center rounded-tr rounded-br py-4 px-2 lg:px-8 w-14 lg:w-32 ${bgLightColor}`}
        >
          {icon}
        </Col>
      </Row>
    );
  };

  return (
    <Row className="border border-solid border-custom_gray">
      <Col span={24} className="py-2 px-4 lg:px-6 bg-custom_lightgray">
        <Title level={4} className="!text-lg lg:!text-2xl">
          {title}
        </Title>
      </Col>

      <Col className="p-4 lg:p-6" span={24}>
        <Form onFinish={handleSaveRange}>
          <Row gutter={32}>
            <Col xs={{ span: 12 }}>
              {!onlyMax && (
                <Row justify="start" className="mb-2">
                  <Col className="flex justify-center items-center py-2 px-8 w-32 md:w-64">
                    {translate("MÁX")}
                  </Col>
                </Row>
              )}
              <Label
                checkbox={{
                  name: "max_value_alert",
                  onChange: handleCheckboxChange,
                  checked: checkboxValues.max_value_alert,
                  value: true,
                }}
                type="exclamation"
              />
              <Label
                checkbox={{
                  name: "max_value",
                  onChange: handleCheckboxChange,
                  checked: checkboxValues.max_value,
                  value: true,
                }}
                type="warning"
              />
              {(!onlyMax || values.type === "hs_alimentacion") && (
                <Label type="check" />
              )}
              {!onlyMax && (
                <>
                  <Label
                    checkbox={{
                      name: "min_value",
                      onChange: handleCheckboxChange,
                      checked: checkboxValues.min_value,
                      value: true,
                    }}
                    type="warning"
                  />
                  <Label
                    checkbox={{
                      name: "min_value_alert",
                      onChange: handleCheckboxChange,
                      checked: checkboxValues.min_value_alert,
                      value: true,
                    }}
                    type="exclamation"
                  />
                  <Row justify="start">
                    <Col className="flex justify-center items-center py-2 px-8 w-32 lg:w-64">
                      {translate("MIN")}
                    </Col>
                  </Row>
                </>
              )}
            </Col>
            <Col
              xs={{ span: 12 }}
              className={`flex flex-col justify-center ${
                onlyMax ? "-mt-2 pt-2 md:pt-0" : ""
              }`}
            >
              <Row className="mb-2" style={{ height: 68 }}>
                <Col className="flex justify-start items-center">
                  <Input
                    type="number"
                    name="max_value_alert"
                    value={values.max_value_alert}
                    onChange={handleChange}
                  />
                </Col>
              </Row>
              <Row className="mb-2" style={{ height: 68 }}>
                <Col className="flex justify-start items-center">
                  <Input
                    type="number"
                    name="max_value"
                    value={values.max_value}
                    onChange={handleChange}
                  />
                </Col>
              </Row>
              <Row className="mb-2" style={{ height: 68 }}>
                <Col></Col>
              </Row>
              {!onlyMax && (
                <>
                  <Row className="mb-2" style={{ height: 68 }}>
                    <Col className="flex justify-start items-center">
                      <Input
                        type="number"
                        name="min_value"
                        value={values.min_value}
                        onChange={handleChange}
                      />
                    </Col>
                  </Row>
                  <Row style={{ height: 68 }}>
                    <Col className="flex justify-start items-center">
                      <Input
                        type="number"
                        name="min_value_alert"
                        value={values.min_value_alert}
                        onChange={handleChange}
                      />
                    </Col>
                  </Row>
                </>
              )}
            </Col>
            {error && (
              <Col span="24" className="text-center text-custom_red mt-4">
                {error}
              </Col>
            )}
            <Col span="24" className="text-center mt-4">
              <PrimaryButton htmlType="submit" loading={loading}>
                {translate("Guardar")}
              </PrimaryButton>
            </Col>
          </Row>
        </Form>
      </Col>
    </Row>
  );
};

export default RangeForm;
